import {
  IssueActionProgressEnum,
  IssueStatusEnum,
} from '@issuemanager/constants';
import {
  GetManageIssueFormSchemaArgs,
  actionApproverValidation,
  actionDescriptionValidation,
  actionDueDateValidation,
  actionDueDateValidations,
  actionOwnerValidation,
  actionSequentialDueDateValidation,
  actionTitleValidations,
  actionsAtLeastOneValidation,
  baseSchema,
} from './common';

export function getManageIssueFormSubmitSchema({
  t,
  timezoneOffset,
  SLAs,
  isDateEarlierThanSLA,
  issueStatus,
  issueActionProgress,
}: GetManageIssueFormSchemaArgs) {
  const isNotYetInitiated =
    issueStatus === IssueStatusEnum.IN_PROGRESS &&
    (issueActionProgress === IssueActionProgressEnum.NOT_INITIATED ||
      issueActionProgress === IssueActionProgressEnum.BLANK);
  /**
   * fields are required except actions at first.
   * then according to the issue-status action's fields are required.
   * the validations that depends on issue-status | issue-progress will be done
   * in superRefine section
   */
  return (
    baseSchema
      .extend({
        ownerId: baseSchema.shape.ownerId.refine((val) => !!val, {
          message: t('issue:form.errorMessages.owner.required'),
        }),
        severity: baseSchema.shape.severity.refine((val) => !!val, {
          message: t('issue:form.errorMessages.severity.required'),
        }),
        dueDate: baseSchema.shape.dueDate.refine((val) => !!val, {
          message: t('issue:form.errorMessages.dueDate.required'),
        }),
        managerId: baseSchema.shape.managerId.refine((val) => !!val, {
          message: t('issue:form.errorMessages.manager.required'),
        }),
        approverId: baseSchema.shape.approverId.refine((val) => !!val, {
          message: t('issue:form.errorMessages.approver.required'),
        }),
      })
      /**
       * these validations required using reference to other fields and outside data
       * validation logic is explicitly defined here to make it more readable
       * every error needs to be push individually to the context
       * the first part checks the Assign validations
       * the second part checks the Workflow validations
       */
      .superRefine((data, ctx) => {
        // Assignment validations

        // Workflow validations
        if (isNotYetInitiated) {
          actionsAtLeastOneValidation({
            actions: data.actions,
            ctx,
            t,
          });

          data.actions?.forEach((action, index) => {
            actionDescriptionValidation({
              data: {
                description: action.description,
                index,
              },
              ctx,
              t,
            });
            actionDueDateValidation({
              data: {
                dueDate: action.dueDate,
                index,
              },
              ctx,
              t,
            });
            actionOwnerValidation({
              data: {
                ownerId: action.ownerId,
                index,
              },
              ctx,
              t,
            });

            actionApproverValidation({
              data: {
                approverId: action.approverId,
                status: action.status,
                index,
              },
              ctx,
              t,
            });
          });
        }

        // [title | unique title | due date past | due date before issue |
        // due date sequential] validations always happen
        const standardizedTitles = new Set<string>();
        data.actions?.forEach((action, index) => {
          actionTitleValidations({
            data: {
              action,
              actions: data.actions || [],
              index,
              standardizedTitles,
            },
            isDateEarlierThanSLA,
            SLAs,
            ctx,
            t,
          });
          actionDueDateValidations({
            data: {
              action,
              dueDate: data.dueDate,
              index,
            },
            timezoneOffset,
            SLAs,
            ctx,
            t,
            skipPastDateValidation: true,
          });
          actionSequentialDueDateValidation({
            data: {
              action,
              actions: data.actions || [],
              dueDate: action.dueDate,
              index,
              executionType: data.executionType,
            },
            ctx,
            t,
          });
        });
      })
  );
}

export type ManageIssueFormSubmitSchemaType = ReturnType<
  typeof getManageIssueFormSubmitSchema
>;
